import { useCallback, useEffect, useRef, useState } from "react";
import {
  APP_BUILD_ID,
  fetchDeployedBuildId,
  isAppVersionCheckEnabled,
  isBuildObsolete,
} from "../appVersion";

export interface UseAppVersionGuardResult {
  runningBuildId: string;
  deployedBuildId: string | null;
  isObsolete: boolean;
  checkNow: () => Promise<boolean>;
}

/**
 * Verifica se existe uma build mais nova publicada, na montagem e sempre que a
 * aba volta ao primeiro plano. Nunca recarrega automaticamente: quem decide é a
 * tela que consome o resultado, para não descartar dados em preenchimento.
 */
export function useAppVersionGuard(): UseAppVersionGuardResult {
  const [deployedBuildId, setDeployedBuildId] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const checkNow = useCallback(async () => {
    if (!isAppVersionCheckEnabled() || inFlightRef.current) return false;
    inFlightRef.current = true;
    try {
      const deployed = await fetchDeployedBuildId();
      if (!mountedRef.current) return false;
      if (deployed) setDeployedBuildId(deployed);
      return isBuildObsolete(APP_BUILD_ID, deployed);
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isAppVersionCheckEnabled()) return undefined;
    void checkNow();

    const handleVisibility = () => {
      if (document.visibilityState === "visible") void checkNow();
    };
    // Quem ficou offline durante um deploy com a aba em primeiro plano só
    // descobriria a build nova no próximo foco; a volta da rede também conta.
    const handleOnline = () => void checkNow();
    window.addEventListener("focus", handleVisibility);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("focus", handleVisibility);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", handleOnline);
    };
  }, [checkNow]);

  return {
    runningBuildId: APP_BUILD_ID,
    deployedBuildId,
    isObsolete: isBuildObsolete(APP_BUILD_ID, deployedBuildId),
    checkNow,
  };
}
